import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

const blogArticles = [
  { label: 'Complete Guide', path: '/blog/litbuy-spreadsheet-complete-guide' },
  { label: 'Review 2026', path: '/blog/litbuy-spreadsheet-review' },
  { label: 'Step-by-Step Tutorial', path: '/blog/how-to-use-litbuy-spreadsheet' },
  { label: 'Best Templates', path: '/blog/best-litbuy-spreadsheet-templates' },
  { label: 'vs Google Sheets', path: '/blog/litbuy-spreadsheet-vs-google-sheets' },
  { label: 'Top Features', path: '/blog/top-features-litbuy-spreadsheet' },
  { label: 'Setup Guide', path: '/blog/litbuy-spreadsheet-setup-guide' },
  { label: 'Common Mistakes', path: '/blog/common-mistakes-litbuy-spreadsheet' },
  { label: 'Advanced Tips', path: '/blog/advanced-tips-litbuy-spreadsheet' },
  { label: 'Best Alternatives', path: '/blog/best-alternatives-litbuy-spreadsheet' },
];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileBlogOpen, setMobileBlogOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blogDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 50);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (blogDropdownRef.current && !blogDropdownRef.current.contains(e.target as Node)) {
        setBlogDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 shadow-sm border-b border-background-200/60'
          : 'bg-background-50/85 backdrop-blur-md'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-lg md:text-xl font-semibold tracking-tight transition-colors ${scrolled ? 'text-foreground-900' : 'text-foreground-900'}`}>
              LitBuy <span className="text-primary-500">Spreadsheets</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>

            {/* Blog Dropdown */}
            <div className="relative" ref={blogDropdownRef}>
              <button
                onClick={() => { setBlogDropdownOpen(!blogDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Guides
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${blogDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {blogDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/70 shadow-xl py-2 z-20">
                  {blogArticles.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      onClick={() => setBlogDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-article-line text-xs text-foreground-400 mr-2.5"></i>
                      {article.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              Shopping Guide
              <i className="ri-external-link-line text-xs"></i>
            </a>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setBlogDropdownOpen(false); }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line"></i>
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-xl py-2 z-20 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-3 whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 cursor-pointer"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer flex items-center gap-3">
              <i className="ri-home-line text-primary-500"></i> Home
            </Link>

            {/* Mobile Blog Dropdown */}
            <button
              onClick={() => setMobileBlogOpen(!mobileBlogOpen)}
              className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer flex items-center justify-between"
            >
              <span className="flex items-center gap-3">
                <i className="ri-article-line text-accent-500"></i> Guides
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileBlogOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileBlogOpen && (
              <div className="ml-8 flex flex-col border-l-2 border-background-200 pl-4">
                {blogArticles.map((article) => (
                  <Link
                    key={article.path}
                    to={article.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-2.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    {article.label}
                  </Link>
                ))}
              </div>
            )}

            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer flex items-center gap-3">
              <i className="ri-shopping-bag-line text-accent-500"></i> Shopping Guide
            </a>

            {/* Mobile Category */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer flex items-center justify-between mt-2"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-primary-500"></i> Categories (14)
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="ml-8 flex flex-col border-l-2 border-background-200 pl-4 max-h-[300px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => { handleCategoryClick(cat.url); setMobileMenuOpen(false); }}
                    className="py-2.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer text-left flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}